import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Search, Filter, Eye, ChevronLeft, ChevronRight, GraduationCap, Wallet } from 'lucide-react';
import { getStudents, updateStudentStatus } from '../lib/api';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 10;

const GRADES = [
  'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6',
  'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12', 'Post School'
];

export default function Students() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await getStudents();
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  // Filter and search
  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      const matchesSearch = searchQuery === '' || 
        `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.email?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesGrade = gradeFilter === 'all' || student.grade === gradeFilter;
      
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && student.isActive !== false) ||
        (statusFilter === 'blocked' && student.isActive === false);
      
      return matchesSearch && matchesGrade && matchesStatus;
    });
  }, [students, searchQuery, gradeFilter, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredStudents.length / ITEMS_PER_PAGE);
  const paginatedStudents = filteredStudents.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, gradeFilter, statusFilter]);

  const handleToggleStatus = async (studentId, currentStatus) => {
    setActionLoading(studentId);
    try {
      const newStatus = currentStatus === false ? true : false;
      await updateStudentStatus(studentId, newStatus);
      setStudents(prev => 
        prev.map(s => s.id === studentId ? { ...s, isActive: newStatus } : s)
      );
      toast.success(`Student ${newStatus ? 'unblocked' : 'blocked'} successfully`);
    } catch (error) {
      toast.error('Failed to update student status');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div data-testid="students-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            Students
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Manage all registered students on the platform
          </p>
        </div>
        <div className="flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-slate-400" />
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {filteredStudents.length} students
          </span>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="student-search-input"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={gradeFilter} onValueChange={setGradeFilter}>
              <SelectTrigger data-testid="student-grade-filter" className="w-full sm:w-40">
                <SelectValue placeholder="Grade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Grades</SelectItem>
                {GRADES.map(grade => (
                  <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="student-status-filter" className="w-full sm:w-40">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Phone</TableHead>
                <TableHead className="font-semibold">Grade</TableHead>
                <TableHead className="font-semibold">Wallet Balance</TableHead>
                <TableHead className="font-semibold">Active</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-12" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  </TableRow>
                ))
              ) : paginatedStudents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-slate-500">
                    No students found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedStudents.map((student) => (
                  <TableRow 
                    key={student.id} 
                    data-testid={`student-row-${student.id}`}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-medium">
                          {student.firstName?.[0]}{student.lastName?.[0]}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900 dark:text-white">
                            {student.firstName} {student.lastName}
                          </div>
                          {student.school && (
                            <div className="text-xs text-slate-500">{student.school}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 dark:text-slate-400">{student.email}</TableCell>
                    <TableCell className="text-slate-600 dark:text-slate-400">{student.phone || '-'}</TableCell>
                    <TableCell>
                      {student.grade ? (
                        <Badge variant="secondary">{student.grade}</Badge>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
                        <Wallet className="w-4 h-4" />
                        R{student.walletBalance?.toFixed(2) || '0.00'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Switch
                        data-testid={`student-toggle-${student.id}`}
                        checked={student.isActive !== false}
                        disabled={actionLoading === student.id}
                        onCheckedChange={() => handleToggleStatus(student.id, student.isActive)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={`student-view-${student.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedStudent(student)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredStudents.length)} of {filteredStudents.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                data-testid="students-prev-page"
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600 dark:text-slate-400 px-2">
                {currentPage} / {totalPages}
              </span>
              <Button
                data-testid="students-next-page"
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Student Detail Modal */}
      <Dialog open={!!selectedStudent} onOpenChange={() => setSelectedStudent(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Student Details</DialogTitle>
            <DialogDescription>
              View and manage student information
            </DialogDescription>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold text-xl">
                  {selectedStudent.firstName?.[0]}{selectedStudent.lastName?.[0]}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    {selectedStudent.firstName} {selectedStudent.lastName}
                  </h3>
                  <p className="text-slate-500">{selectedStudent.school || 'Student'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Email</span>
                  <p className="font-medium text-slate-900 dark:text-white">{selectedStudent.email}</p>
                </div>
                <div>
                  <span className="text-slate-500">Phone</span>
                  <p className="font-medium text-slate-900 dark:text-white">{selectedStudent.phone || 'Not provided'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Grade</span>
                  <p className="font-medium text-slate-900 dark:text-white">{selectedStudent.grade || 'Not specified'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Wallet Balance</span>
                  <p className="font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <Wallet className="w-4 h-4" />
                    R{selectedStudent.walletBalance?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div>
                  <span className="text-slate-500">Date of Birth</span>
                  <p className="font-medium text-slate-900 dark:text-white">{selectedStudent.dateOfBirth || 'Not provided'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Gender</span>
                  <p className="font-medium text-slate-900 dark:text-white">{selectedStudent.gender || 'Not specified'}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-slate-500">Address</span>
                  <p className="font-medium text-slate-900 dark:text-white">{selectedStudent.address || 'Not provided'}</p>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  data-testid="student-modal-toggle"
                  onClick={() => handleToggleStatus(selectedStudent.id, selectedStudent.isActive)}
                  disabled={actionLoading === selectedStudent.id}
                  variant={selectedStudent.isActive !== false ? "destructive" : "default"}
                >
                  {selectedStudent.isActive !== false ? 'Block Student' : 'Unblock Student'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setSelectedStudent(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
